import React, { useState, useCallback, useEffect, useRef } from "react"
import { Stack, Button, Alert, Text } from "@mantine/core"
import { IconAlertTriangle } from "@tabler/icons-react"
import { ForgeRockFormProps, ForgeRockCallback } from "@models/forgeRock.model"
import { ForgeRockStep } from "@services/forgeRockAuthService"
import { LoginError } from "@models/error/login-error"
import { useForgeRockNavigation } from "@hooks/useForgeRockNavigation"
import { useForgeRockSubmission } from "@hooks/useForgeRockSubmission"
import { useMFASubmission } from "@hooks/useMFASubmission"
import { ForgeRockCallbackRenderer } from "./components/ForgeRockCallbackRenderer"
import { ForgeRockErrorDisplay } from "./components/ForgeRockErrorDisplay"
import { CredentialsInputs } from "./components/CredentialsInputs"
import {
	extractCallbackMessage,
	isErrorMessage,
	parseForgeRockErrorMessage,
	detectMFACallbacks,
	isDeviceProfileOnlyStep
} from "@utils/forgeRock.util"
import { FORGEROCK_ERROR_CODES } from "@utils/constants.util"
import { MFAModal } from "@components/ui/MFAModal/MFAModal"
import styles from "../LoginForm/LoginForm.module.css"

/**
 * Maps ForgeRock error types to application error codes
 */
const getErrorCodeFromType = (errorType: string): string => {
	const errorTypeMap: Record<string, string> = {
		INVALID_CREDENTIALS: FORGEROCK_ERROR_CODES.INVALID_CREDENTIALS,
		ACCOUNT_LOCKED: FORGEROCK_ERROR_CODES.ACCOUNT_LOCKED,
		INVALID_USERNAME: FORGEROCK_ERROR_CODES.INVALID_USERNAME
	}

	return errorTypeMap[errorType] || FORGEROCK_ERROR_CODES.INVALID_TOKEN
}

/**
 * Processes callbacks to extract and set error messages
 */
const processErrorCallbacks = (
	callbacks: ForgeRockCallback[],
	setError: (error: LoginError) => void
): void => {
	for (const callback of callbacks) {
		const callbackType = callback.getType?.() || callback.type
		if (callbackType !== "TextOutputCallback") continue

		const message = extractCallbackMessage(callback)
		if (!message || !isErrorMessage(callback, message)) continue

		const parsed = parseForgeRockErrorMessage(message)
		if (parsed.errorType && parsed.userFriendlyMessage) {
			const errorCode = getErrorCodeFromType(parsed.errorType)
			setError(
				new LoginError(
					parsed.userFriendlyMessage,
					errorCode,
					parsed.remainingAttempts ?? 0
				)
			)
		} else {
			setError(new LoginError(message, "FORGEROCK_CALLBACK_ERROR", 0))
		}
	}
}

export const ForgeRockForm: React.FC<ForgeRockFormProps> = ({
	onSuccess,
	onError,
	loginFormComponentData,
	onAssistanceModalOpen,
	error: initialError
}) => {
	const {
		currentStep,
		currentStepNumber,
		callbackValues,
		storeStep,
		saveCurrentStepState,
		updateCallbackValue,
		logStepInfo,
		logStepsHistory,
		navigationMethods,
		resetJourney
	} = useForgeRockNavigation(onError, false)

	const [submissionError, setSubmissionError] = useState<LoginError | null>(
		initialError || null
	)

	// Sync external error prop with internal state (only when error is provided)
	useEffect(() => {
		if (initialError !== undefined) {
			setSubmissionError(initialError)
			hasInitialErrorRef.current = !!initialError
		}
	}, [initialError])

	// Handler to clear error and restart journey
	const handleRetryAfterError = useCallback(async () => {
		setSubmissionError(null)
		hasInitialErrorRef.current = false
		sessionStorage.removeItem("oauth_error")
		await resetJourney()
	}, [resetJourney])

	const lastProcessedStepRef = useRef<number>(-1)
	const hasInitialErrorRef = useRef<boolean>(!!initialError)
	const [mfaModalOpened, setMfaModalOpened] = useState(false)
	const [mfaCallbacks, setMfaCallbacks] =
		useState<ReturnType<typeof detectMFACallbacks>>(null)
	const deviceProfileSubmittingRef = useRef<boolean>(false)
	const loginSuccessTriggeredRef = useRef<boolean>(false)

	const preserveCredentials = useCallback(
		(
			step: ForgeRockStep,
			newCallbackValues: Record<number, string>,
			stepNumber: number
		): Record<number, string> => {
			if (stepNumber === 0) return newCallbackValues

			const loginStep = navigationMethods?.getStep?.(0)
			const loginValues = loginStep?.callbackValues || {}
			const username = loginValues[0]
			const password = loginValues[1]

			if (!username && !password) return newCallbackValues

			const mfaCallbacks = detectMFACallbacks(step)
			const isMFAIndex = (index: number) =>
				mfaCallbacks &&
				(mfaCallbacks.otpCallbackIndex === index ||
					mfaCallbacks.choiceCallbackIndex === index ||
					mfaCallbacks.rememberDeviceCallbackIndex === index)

			const getCallbackType = (index: number) => {
				const callback = step.callbacks?.[index]
				return callback?.getType?.() || callback?.type
			}

			const merged = { ...newCallbackValues }

			if (username && !isMFAIndex(0) && getCallbackType(0) !== "NameCallback") {
				if (!mfaCallbacks || merged[0] === undefined) {
					merged[0] = username
				}
			}

			if (
				password &&
				!isMFAIndex(1) &&
				getCallbackType(1) !== "PasswordCallback"
			) {
				if (!mfaCallbacks || merged[1] === undefined) {
					merged[1] = password
				}
			}

			return merged
		},
		[navigationMethods]
	)

	const handleStepUpdate = useCallback(
		(
			stepNumber: number,
			step: ForgeRockStep,
			newCallbackValues: Record<number, string>
		) => {
			// Don't set lastProcessedStepRef here - let the useEffect handle it
			// This allows the useEffect to detect DeviceProfileCallback-only steps
			// Clear error when user successfully moves to a new step (not step 0)
			// This indicates they've started a new login attempt
			if (stepNumber > 0) {
				setSubmissionError(null)
				hasInitialErrorRef.current = false
			}

			const mergedCallbackValues = preserveCredentials(
				step,
				newCallbackValues,
				stepNumber
			)
			storeStep(stepNumber, step, mergedCallbackValues)

			if (navigationMethods?.navigateToStep) {
				navigationMethods.navigateToStep(stepNumber)
			}

			const mfaCallbacks = detectMFACallbacks(step)
			if (mfaCallbacks) {
				setMfaCallbacks(mfaCallbacks)
				setMfaModalOpened(true)
			} else {
				setMfaModalOpened(false)
				setMfaCallbacks(null)
			}

			if (step.callbacks) {
				processErrorCallbacks(step.callbacks, setSubmissionError)
			}
		},
		[storeStep, navigationMethods, preserveCredentials]
	)

	const {
		submitOTP,
		resendOTP,
		sendSMS,
		isLoading: mfaLoading,
		error: mfaError,
		setError: setMfaError
	} = useMFASubmission({
		currentStep,
		currentStepNumber,
		mfaCallbacks,
		onStepUpdate: handleStepUpdate,
		onSuccess: onSuccess || (() => {}),
		onError,
		logStepInfo
	})

	const {
		handleSubmit,
		autoSubmitDeviceProfile,
		isLoading,
		error: submissionFormError
	} = useForgeRockSubmission({
		currentStep,
		currentStepNumber,
		callbackValues,
		saveCurrentStepState,
		storeStep,
		logStepInfo,
		logStepsHistory,
		onSuccess,
		onError,
		onStepUpdate: handleStepUpdate
	})

	// Handle LoginSuccess steps without callbacks (e.g., when authenticate returns tokenId)
	useEffect(() => {
		if (
			currentStep?.type === "LoginSuccess" &&
			(!currentStep.callbacks || currentStep.callbacks.length === 0) &&
			!loginSuccessTriggeredRef.current
		) {
			// Automatically trigger success callback for LoginSuccess without callbacks
			// This handles the case where /authenticate returns tokenId, successUrl, realm
			loginSuccessTriggeredRef.current = true
			if (onSuccess) {
				onSuccess()
			}
			return
		}
		// Reset the ref when step changes (but not to LoginSuccess)
		if (currentStep?.type !== "LoginSuccess") {
			loginSuccessTriggeredRef.current = false
		}
	}, [currentStep, onSuccess])

	useEffect(() => {
		if (!currentStep?.callbacks) return
		if (lastProcessedStepRef.current === currentStepNumber) return
		if (deviceProfileSubmittingRef.current) return // Prevent re-submission while already submitting

		lastProcessedStepRef.current = currentStepNumber

		if (currentStepNumber > 0 || !hasInitialErrorRef.current) {
			setSubmissionError(null)
		}

		// Check for DeviceProfileCallback-only step and auto-submit
		if (isDeviceProfileOnlyStep(currentStep)) {
			deviceProfileSubmittingRef.current = true
			autoSubmitDeviceProfile()
				.then(() => {
					deviceProfileSubmittingRef.current = false
				})
				.catch((error) => {
					deviceProfileSubmittingRef.current = false
					onError?.(error as Error)
				})
			return
		}

		const detectedMFACallbacks = detectMFACallbacks(currentStep)
		if (detectedMFACallbacks) {
			setMfaCallbacks(detectedMFACallbacks)
			setMfaModalOpened(true)
			return
		}

		setMfaModalOpened(false)
		setMfaCallbacks(null)

		processErrorCallbacks(currentStep.callbacks, setSubmissionError)
	}, [currentStep, currentStepNumber, autoSubmitDeviceProfile, onError])

	const error = submissionFormError || submissionError
	const isDeviceProfileStep = isDeviceProfileOnlyStep(currentStep)
	const shouldShowLoadingState =
		isLoading ||
		mfaLoading ||
		(!currentStep && !error) ||
		currentStep?.type === "LoginSuccess" ||
		isDeviceProfileStep
	const shouldDisableInputs = shouldShowLoadingState

	const getCredentialsFromLoginStep = useCallback(() => {
		const loginStep = navigationMethods?.getStep?.(0)
		const loginValues = loginStep?.callbackValues || {}
		return {
			username: (loginValues[0] ?? callbackValues[0] ?? "") as string,
			password: (loginValues[1] ?? callbackValues[1] ?? "") as string
		}
	}, [navigationMethods, callbackValues])

	if (mfaModalOpened && mfaCallbacks) {
		const { username, password } = getCredentialsFromLoginStep()

		return (
			<>
				<form className={styles.loginForm}>
					<Stack gap="lg">
						<CredentialsInputs
							username={username}
							password={password}
							disabled={true}
							readOnly={true}
							loginFormComponentData={loginFormComponentData}
						/>
						<Button
							type="submit"
							variant="dark-blue"
							size="xl"
							fullWidth
							loading={true}
							disabled
							className={styles.loginButton}
							data-qa="ushome.button.login"
						>
							Loading...
						</Button>
					</Stack>
				</form>
				<MFAModal
					opened={mfaModalOpened}
					username={username}
					onClose={async () => {
						if (mfaLoading) return

						setMfaError(null)
						setMfaModalOpened(false)
						setMfaCallbacks(null)
						lastProcessedStepRef.current = -1

						try {
							await resetJourney()
							logStepInfo("MFA_MODAL_CLOSED_JOURNEY_RESET", 0)
						} catch (error) {
							onError?.(error as Error)
						}
					}}
					onSubmit={async (otp: string, rememberDevice: boolean) => {
						// submitOTP handles setting the rememberDevice value on BooleanAttributeInputCallback
						await submitOTP(otp, rememberDevice)
					}}
					onResendOTP={async () => {
						await resendOTP()
					}}
					onSendSMS={async () => {
						await sendSMS()
					}}
					onSuccess={() => {
						setMfaModalOpened(false)
						setMfaCallbacks(null)
						onSuccess?.()
					}}
					isLoading={mfaLoading}
					error={mfaError?.message || ""}
					mfaConfig={mfaCallbacks?.textOutputConfig ?? undefined}
				/>
			</>
		)
	}

	// If we have no step but have an error (journey was skipped), show form with retry option
	if (!currentStep && error) {
		return (
			<form
				className={styles.loginForm}
				onSubmit={(e) => {
					e.preventDefault()
					handleRetryAfterError()
				}}
			>
				<Stack gap="lg">
					<CredentialsInputs
						username=""
						password=""
						disabled={false}
						readOnly={true}
						loginFormComponentData={loginFormComponentData}
					/>
					{error && (
						<ForgeRockErrorDisplay
							error={error}
							loginFormComponentData={loginFormComponentData}
							onAssistanceModalOpen={onAssistanceModalOpen}
						/>
					)}
					<Button
						type="submit"
						variant="dark-blue"
						size="xl"
						fullWidth
						className={styles.loginButton}
						data-qa="ushome.button.login"
					>
						{loginFormComponentData?.signInButton}
					</Button>
				</Stack>
			</form>
		)
	}

	// If we have no step and no error, show loading form
	if (!currentStep && !error) {
		return (
			<form className={styles.loginForm}>
				<Stack gap="lg">
					<CredentialsInputs
						username=""
						password=""
						disabled={true}
						loginFormComponentData={loginFormComponentData}
					/>
					<Button
						type="submit"
						variant="dark-blue"
						size="xl"
						fullWidth
						loading={true}
						className={styles.loginButton}
						data-qa="ushome.button.login"
					>
						Loading...
					</Button>
				</Stack>
			</form>
		)
	}

	const allCallbacks = currentStep?.callbacks || []
	const inputCallbacks = allCallbacks.filter((callback: ForgeRockCallback) => {
		const type = callback.getType?.() || callback.type
		return type === "NameCallback" || type === "PasswordCallback"
	})

	// Show loading state for DeviceProfileCallback-only steps
	if (isDeviceProfileOnlyStep(currentStep)) {
		const { username, password } = getCredentialsFromLoginStep()
		return (
			<form className={styles.loginForm}>
				<Stack gap="lg">
					<CredentialsInputs
						username={username}
						password={password}
						disabled={true}
						loginFormComponentData={loginFormComponentData}
					/>
					<Button
						type="button"
						variant="dark-blue"
						size="xl"
						fullWidth
						loading={true}
						disabled
						className={styles.loginButton}
						data-qa="ushome.button.login"
					>
						Collecting device information...
					</Button>
				</Stack>
			</form>
		)
	}

	if (inputCallbacks?.length || currentStep?.type === "LoginSuccess") {
		const credentials = getCredentialsFromLoginStep()
		return (
			<form onSubmit={handleSubmit} className={styles.loginForm}>
				<Stack gap="lg">
					{inputCallbacks.length > 0 ? (
						inputCallbacks.map((callback: ForgeRockCallback, index: number) => (
							<ForgeRockCallbackRenderer
								key={`callback-${index}-step-${currentStepNumber}`}
								callback={callback}
								index={index}
								currentStepNumber={currentStepNumber}
								callbackValues={callbackValues}
								updateCallbackValue={updateCallbackValue}
								isLoading={isLoading}
								hasError={!!error}
								loginFormComponentData={loginFormComponentData}
							/>
						))
					) : (
						<CredentialsInputs
							username={credentials.username}
							password={credentials.password}
							disabled={shouldDisableInputs}
							loginFormComponentData={loginFormComponentData}
						/>
					)}
					{error && (
						<ForgeRockErrorDisplay
							error={error}
							loginFormComponentData={loginFormComponentData}
							onAssistanceModalOpen={onAssistanceModalOpen}
						/>
					)}
					<Button
						type="submit"
						variant="dark-blue"
						size="xl"
						fullWidth
						loading={shouldShowLoadingState}
						disabled={shouldShowLoadingState}
						className={styles.loginButton}
						data-qa="ushome.button.login"
					>
						{shouldShowLoadingState
							? "Loading..."
							: loginFormComponentData?.signInButton}
					</Button>
				</Stack>
			</form>
		)
	}

	// Fallback for unexpected states
	return (
		<form className={styles.loginForm}>
			<Stack gap="lg">
				<Alert
					variant="filled"
					color="red"
					icon={<IconAlertTriangle />}
					radius={30}
				>
					<Text fz={12}>Unable to load authentication form</Text>
				</Alert>
			</Stack>
		</form>
	)
}
